import { useEffect, useRef } from 'react';
import { isPendingTask, type TaskSummary } from '../../lib/tasks';

/**
 * How long a row stays on screen after it is ticked off, while completed work
 * is hidden.
 *
 * There has to be a pause. Taking the row away in the same paint as the click
 * is the one thing that reads as a bug rather than a filter: the row you were
 * aiming at is gone before it has said it is done, and a mis-click has nothing
 * left to undo. Five seconds is long enough to watch the title cross itself out
 * and to put it back, and short enough that the list settles on its own instead
 * of carrying finished work until the next reload.
 */
const DONE_ROW_LINGER_MS = 5_000;

/**
 * Take finished rows out of a list that is not showing finished work, a few
 * seconds after they finish.
 *
 * The fetch already drops DONE (see each page's `showDone`), so a completed row
 * is only ever in the list because it was completed *after* the list arrived --
 * from the row's ✓, the status menu, the bulk bar or the panel. Left alone it
 * sits crossed out until something else re-fetches, which reads as the toggle
 * having quietly stopped working; this is the filter catching up with itself.
 *
 * Keyed off the rows rather than hooked into the patch, so it covers all of
 * those paths at once and un-ticking inside the five seconds cancels for free:
 * the row is no longer DONE, so the effect no longer sees it and its timer is
 * cleared on the way past.
 *
 * Shared by both task lists rather than copied into each, for the reason
 * useTaskListState gives at more length: the failure mode of a second copy is
 * not a compile error, it is one page drifting into hiding rows at a different
 * moment -- or not cancelling a timer -- a year from now.
 */
export function useHideCompleted({
  tasks,
  showDone,
  activeTaskId,
  removeTask,
}: {
  tasks: TaskSummary[];
  /** When true this does nothing at all: the list is showing finished work on
   *  purpose. */
  showDone: boolean;
  /** The row the open panel is showing, exempt while it is open. Pulling it out
   *  would take the seed from under the panel and the next edit in there would
   *  put it straight back; it gets its five seconds once closed. */
  activeTaskId: string | null;
  /** Must be referentially stable -- it is a dependency of the effect below.
   *  `useTaskListState`'s is. */
  removeTask: (taskId: string) => void;
}): void {
  /**
   * One pending removal per ticked-off row.
   *
   * A Map rather than a single timer because each row owns its own five
   * seconds: ticking a second task four seconds after the first must not hand
   * the first one another five, and a steady march down a list would otherwise
   * never clear anything at all.
   */
  const hideTimers = useRef(new Map<string, number>());

  useEffect(() => {
    const timers = hideTimers.current;

    const doomed = new Set(
      showDone
        ? []
        : tasks
            .filter(
              (task) =>
                task.status === 'DONE' && !isPendingTask(task.id) && task.id !== activeTaskId,
            )
            .map((task) => task.id),
    );

    for (const [taskId, timer] of timers) {
      if (doomed.has(taskId)) continue;
      window.clearTimeout(timer);
      timers.delete(taskId);
    }

    for (const taskId of doomed) {
      if (timers.has(taskId)) continue;
      timers.set(
        taskId,
        window.setTimeout(() => {
          timers.delete(taskId);
          removeTask(taskId);
        }, DONE_ROW_LINGER_MS),
      );
    }
  }, [tasks, showDone, activeTaskId, removeTask]);

  /** Nothing to put back on the way out, but a timer that outlives the page
   *  would still call into it. */
  useEffect(() => {
    const timers = hideTimers.current;
    return () => {
      for (const timer of timers.values()) window.clearTimeout(timer);
      timers.clear();
    };
  }, []);
}
